/* eslint-disable max-lines-per-function */
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Arbeitsbereich } from '@/domain/dienstplan/shared/types/Arbeitsbereich';
import { getColorsForMarkierung } from '@/domain/dienstplan/shared/utils/getColorForMarkierung';
import { cn } from '@/lib/utils';
import { faCheck, faAnglesUpDown } from '@fortawesome/pro-regular-svg-icons';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CSSProperties, useState } from 'react';
import { useTranslation } from 'react-i18next';

type StyleProps = CSSProperties & {
  '--dot-color': CSSProperties['color'];
};

export function ArbeitsbereichSelect({
  arbeitsbereiche,
  onSelect,
  defaultArbeitsbereich,
}: {
  arbeitsbereiche: Arbeitsbereich[];
  onSelect?: (arbeitsbereich: Arbeitsbereich) => void;
  defaultArbeitsbereich: Arbeitsbereich | undefined;
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(defaultArbeitsbereich?.id ?? '');
  const { t } = useTranslation();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          role="combobox"
          aria-expanded={open}
          variant="ghost"
          className="bg-neutrals-100 border-solid border border-neutrals-300 text-neutrals-900 flex justify-between w-56 font-normal">
          {value ? (
            <div className="flex justify-start items-center gap-2">
              {
                <FontAwesomeIcon
                  icon={faCircle}
                  size="xs"
                  className={`text-(--dot-color)`}
                  style={
                    {
                      '--dot-color': getColorsForMarkierung(
                        arbeitsbereiche.find(
                          (arbeitsbereich) => arbeitsbereich.id === value,
                        ).markierung,
                      ).base,
                    } as StyleProps
                  }
                />
              }
              <span className="font-normal max-w-36 truncate">
                {
                  arbeitsbereiche.find(
                    (arbeitsbereich) => arbeitsbereich.id === value,
                  ).name
                }
              </span>
            </div>
          ) : (
            <span className="font-normal">
              {t('domain.dienstplan.arbeitsbereiche.all')}
            </span>
          )}
          <span className="flex flex-col gap-0">
            <FontAwesomeIcon icon={faAnglesUpDown} />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="bg-neutrals-500 p-0 max-w-72">
        <Command
          filter={(value, search) => {
            const bereich = arbeitsbereiche.find((ab) => ab.id == value);

            if (
              bereich &&
              bereich?.name
                .toLocaleLowerCase()
                .includes(search.toLocaleLowerCase())
            ) {
              return 1;
            }
            return 0;
          }}>
          <CommandInput placeholder="Suchen..." />
          <CommandList>
            <CommandEmpty className="bg-neutrals-100 flex justify-center pb-3">
              {t('domain.dienstplan.arbeitsbereiche.notFound')}
            </CommandEmpty>
            <CommandGroup>
              <CommandItem
                key="delete"
                value="a"
                onSelect={() => {
                  setValue('');
                  onSelect(null);
                  setOpen(false);
                }}>
                <span className="font-normal">
                  {t('domain.dienstplan.arbeitsbereiche.all')}
                </span>{' '}
              </CommandItem>
              {arbeitsbereiche.map((arbeitsbereich) => (
                <CommandItem
                  key={arbeitsbereich.id}
                  value={arbeitsbereich.id}
                  onSelect={(currentValue) => {
                    setValue((value) =>
                      currentValue === value ? '' : currentValue,
                    );
                    onSelect(
                      arbeitsbereiche.find((ab) => ab.id === currentValue),
                    );
                    setOpen(false);
                  }}>
                  <div className="flex gap-2 items-center justify-start">
                    <FontAwesomeIcon
                      icon={faCircle}
                      size="xs"
                      className={`text-(--dot-color)`}
                      style={
                        {
                          '--dot-color': getColorsForMarkierung(
                            arbeitsbereich.markierung,
                          ).base,
                        } as StyleProps
                      }
                    />
                    <span className="font-normal">{arbeitsbereich.name}</span>
                  </div>
                  <FontAwesomeIcon
                    icon={faCheck}
                    className={cn(
                      'ml-auto text-primary-blue-600',
                      value === arbeitsbereich.id ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
